import { memo, useEffect, useMemo, useState } from "react";
import type { ProviderId } from "../lib/api";

export type TimelineEventKind = "first" | "update" | "final";

export interface TimelineEvent {
  id: number;
  atMs: number;
  latencyMs?: number;
  text: string;
  kind: TimelineEventKind;
}

interface TimelineRow {
  id: ProviderId;
  label: string;
  events: TimelineEvent[];
}

interface LatencyTimelineProps {
  rows: TimelineRow[];
  startedAt: number | null;
  endedAtMs: number | null;
  running: boolean;
}

export const TIMELINE_WINDOW_MS = 10_000;
const TICK_INTERVAL_MS = 2_000;

export const timelineWindowStart = (elapsedMs: number): number =>
  Math.max(0, elapsedMs - TIMELINE_WINDOW_MS);

export const timelinePosition = (atMs: number, windowStartMs: number): number =>
  Math.max(0, Math.min(100, ((atMs - windowStartMs) / TIMELINE_WINDOW_MS) * 100));

const formatTimelineMs = (value: number): string =>
  value < 1_000 ? `${Math.round(value)} ms` : `${(value / 1_000).toFixed(2)} s`;

function LatencyTimelineView({
  rows,
  startedAt,
  endedAtMs,
  running,
}: LatencyTimelineProps) {
  const [liveElapsedMs, setLiveElapsedMs] = useState(0);

  useEffect(() => {
    if (startedAt === null) {
      setLiveElapsedMs(0);
      return;
    }
    const update = () => setLiveElapsedMs(Math.max(0, performance.now() - startedAt));
    update();
    if (!running) return;
    const timer = window.setInterval(update, 100);
    return () => window.clearInterval(timer);
  }, [running, startedAt]);

  const latestEventMs = useMemo(() => {
    let latest = 0;
    for (const row of rows) {
      for (const event of row.events) latest = Math.max(latest, event.atMs);
    }
    return latest;
  }, [rows]);

  const elapsedMs = Math.max(endedAtMs ?? liveElapsedMs, latestEventMs);
  const windowStartMs = timelineWindowStart(elapsedMs);
  const firstEvents = useMemo(
    () => rows.flatMap((row) => row.events
      .filter((event) => event.kind === "first" && event.latencyMs !== undefined)
      .map((event) => ({ ...event, providerId: row.id, providerLabel: row.label }))),
    [rows],
  );
  const averageLatencyMs = firstEvents.length
    ? firstEvents.reduce((sum, event) => sum + (event.latencyMs ?? 0), 0) / firstEvents.length
    : null;
  const averageAtMs = firstEvents.length
    ? firstEvents.reduce((sum, event) => sum + event.atMs, 0) / firstEvents.length
    : null;
  const leader = firstEvents.reduce<(typeof firstEvents)[number] | null>((fastest, event) =>
    !fastest || (event.latencyMs ?? Infinity) < (fastest.latencyMs ?? Infinity) ? event : fastest, null);

  const ticks = useMemo(() => {
    const firstTick = Math.ceil(windowStartMs / TICK_INTERVAL_MS) * TICK_INTERVAL_MS;
    const values: number[] = [];
    for (let tick = firstTick; tick <= windowStartMs + TIMELINE_WINDOW_MS; tick += TICK_INTERVAL_MS) {
      values.push(tick);
    }
    return values;
  }, [windowStartMs]);

  const averageVisible = averageAtMs !== null
    && averageAtMs >= windowStartMs
    && averageAtMs <= windowStartMs + TIMELINE_WINDOW_MS;

  return (
    <section className="latency-timeline" aria-label="Shared provider timing">
      <header className="latency-timeline__header">
        <div>
          <h2>Arrival timeline</h2>
          <p>Every word update uses the same clock. Older events drift left.</p>
        </div>
        <dl className="timeline-summary">
          <div>
            <dt>Leader</dt>
            <dd>{leader ? `${leader.providerLabel} · ${formatTimelineMs(leader.latencyMs ?? 0)}` : "Waiting"}</dd>
          </div>
          <div>
            <dt>Mean first text</dt>
            <dd>{averageLatencyMs === null ? "Waiting" : formatTimelineMs(averageLatencyMs)}</dd>
          </div>
        </dl>
      </header>

      <div className="timeline-scale" aria-hidden="true">
        <span className="timeline-scale__spacer" />
        <div className="timeline-scale__track">
          {ticks.map((tick) => (
            <span
              className="timeline-scale__tick"
              key={tick}
              style={{ left: `${timelinePosition(tick, windowStartMs)}%` }}
            >
              {`${(tick / 1_000).toFixed(0)}s`}
            </span>
          ))}
        </div>
      </div>

      <div className="timeline-race">
        <div className="timeline-race__overlay" aria-hidden="true">
          {averageVisible && (
            <span
              className="timeline-average-marker"
              style={{ left: `${timelinePosition(averageAtMs, windowStartMs)}%` }}
            />
          )}
        </div>
        {rows.map((row) => (
          <div className="timeline-row" key={row.id}>
            <span className="timeline-row__label">{row.label}</span>
            <div className="timeline-row__track">
              {(() => {
                const visibleEvents = row.events.filter((event) => event.atMs >= windowStartMs - 250);
                return visibleEvents.map((event, index) => {
                  const age = Math.max(0, elapsedMs - event.atMs);
                  const opacity = Math.max(0.22, 1 - age / TIMELINE_WINDOW_MS);
                  const nextEvent = visibleEvents[index + 1];
                  const position = timelinePosition(event.atMs, windowStartMs);
                  const nextPosition = nextEvent
                    ? timelinePosition(nextEvent.atMs, windowStartMs)
                    : 100;
                  return (
                    <span
                      className={`timeline-event timeline-event--${event.kind}`}
                      key={event.id}
                      style={{
                        left: `${position}%`,
                        right: `${Math.max(0, 100 - nextPosition)}%`,
                        opacity,
                      }}
                      title={`${row.label}: ${event.text} at ${formatTimelineMs(event.atMs)}`}
                    >
                      <i aria-hidden="true" />
                      <b>{event.text}</b>
                    </span>
                  );
                });
              })()}
              {startedAt !== null && (
                <span
                  className="timeline-playhead"
                  style={{ left: `${timelinePosition(elapsedMs, windowStartMs)}%` }}
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export const LatencyTimeline = memo(LatencyTimelineView);
